import React, { useState, useEffect } from "react";
import "alpinejs";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";

export const HeaderActions = ({ item_count, settings }) => {
    const isMobile = useMobileBreakpoint(settings.breakpoint);

    const { section } = settings;

    const [cartItemCount, setCartItemCount] = useState(0);
    let isCustomerLoggedIn = settings.globals.is_customer_logged_in;
    let showInitials = true;

    // Customer name for initials
    const customerName = settings.globals.customer_name;
    const customerInitials = customerName && customerName.trim().length > 0 ? customerName.trim()[0] : '';

    // Font styles for text display
    const text_style = section.header_font_type || 'b2';

    // Get the configurable text values with defaults
    const accountText = section.account_action_text || "ACCOUNT";
    const cartText = section.cart_action_text || "CART";
    const wishlistText = section.wishlist_action_text || "WISHLIST";
    
    // Get the search block settings
    const searchBlock = settings.blocks.find(block => block.type === "search_bar") || {};
    const searchText = searchBlock.search_action_text || "SEARCH";

    // Cart counter styling
    const cartCounterStyles = {
        '--cart-counter-radius': `${section.cart_counter_radius || 12}px`,
        '--cart-counter-bg-color': section.cart_counter_bg_color || '#000',
        '--cart-counter-text-color': section.cart_counter_text_color || '#fff',
        'gap': `${section.header_actions_spacing || 24}px`
    } as React.CSSProperties;

    useEffect(() => {
        const fetchCart = async (event) => {
            try {
                const response = await fetch(`/cart.js`);
                const cartData = await response.json();
                const updatedItemCount = cartData.item_count || 0;
                setCartItemCount(updatedItemCount);
            } catch (error) {
                console.error("Error fetching cart:", error);
            }
        };

        document.addEventListener("liquid-ajax-cart:request-end", function(event: any) {
            fetchCart({ detail: {} });
        });

        fetchCart({ detail: {} });
        
        // Clean up the event listener when the component is unmounted
        return () => {
            document.removeEventListener('liquid-ajax-cart:request-end', () => {});
        };
    }, []);

    // Function to toggle minicart
    const toggleMinicart = () => {
        if (window.Alpine && window.Alpine.store('global')) {
            window.Alpine.store('global').toggleMinicart();
        }
    };

    return (
        <div
            className="flex items-center justify-end header-toolbar z-40 min-h-[48px] shrink-0"
            style={cartCounterStyles}
        >
          
            {/* Wishlist */}
            {settings.globals.enable_wishlist === "true" && section.show_wishlist_in_header && (
                <a href="/pages/wishlist" className="antialiased [&>svg]:h-[28px]">
                    {section.show_text_no_icons_wishlist ? (
                        <span className={`${text_style}`}>{wishlistText}</span>
                    ) : (
                        <i className="icon-wishlist mobile_menu-icon"></i>
                    )}
                </a>
            )}

            {/* Account - Logged In with Popup */}
            {isCustomerLoggedIn && section.use_account_popup && (
                <div
                    className="account-default-popup account-icon antialiased [&>svg]:h-[28px] no-underline cursor-pointer">
                    {section.account_popup_trigger === "click" && showInitials && !isMobile ? (
                        <div
                            className="flex items-center justify-center text-[16px] bg-[#000000] rounded-full text-white w-[30px] h-[30px] uppercase"
                            onClick={() => window.dispatchEvent(new CustomEvent('toggle-display', { detail: { message: 'header-account' } }))}>
                            <span>{customerInitials}</span>
                        </div>
                    ) : section.show_text_no_icons_account && !isMobile ? (
                        <span 
                            className={`${text_style}`}
                            onClick={() => window.dispatchEvent(new CustomEvent('toggle-display', { detail: { message: 'header-account' } }))}>
                            {accountText}
                        </span>
                    ) : !isMobile && (
                        <i className={`${section.account_icon || 'icon-user-circle'} mobile_menu-icon`}
                           onClick={() => window.dispatchEvent(new CustomEvent('toggle-display', { detail: { message: 'header-account' } }))}>
                        </i>
                    )}
                </div>
            )}

            {/* Account - Not Logged In with Hover Popup */}
            {!isCustomerLoggedIn &&
                !isMobile &&
                section.use_account_popup &&
                section.account_popup_trigger === "hover" && (
                    section.show_text_no_icons_account ? (
                        <div className="account-hover-popup account-icon antialiased [&>svg]:h-[28px]"
                             onMouseEnter={() => window.dispatchEvent(new CustomEvent('toggle-display-open', { detail: { message: 'header-account' } }))}
                             onMouseLeave={() => window.dispatchEvent(new CustomEvent('toggle-display-close', { detail: { message: 'header-account' } }))}>
                            <span className={`${text_style}`}>{accountText}</span>
                        </div>
                    ) : (
                        <div className="account-hover-popup account-icon antialiased [&>svg]:h-[28px]"
                             onMouseEnter={() => window.dispatchEvent(new CustomEvent('toggle-display-open', { detail: { message: 'header-account' } }))}
                             onMouseLeave={() => window.dispatchEvent(new CustomEvent('toggle-display-close', { detail: { message: 'header-account' } }))}>
                            <i className={`${section.account_icon || 'icon-user-circle'} mobile_menu-icon`}></i>
                        </div>
                    )
                )}

            {/* Account - Not Logged In with Click Popup */}
            {!isCustomerLoggedIn &&
                !isMobile &&
                section.use_account_popup &&
                section.account_popup_trigger === "click" && (
                    section.show_text_no_icons_account ? (
                        <div className="account-click-popup account-icon antialiased [&>svg]:h-[28px]"
                             onClick={() => window.dispatchEvent(new CustomEvent('toggle-display', { detail: { message: 'header-account' } }))}>
                            <span className={`${text_style}`}>{accountText}</span>
                        </div>
                    ) : (
                        <div className="account-click-popup account-icon antialiased [&>svg]:h-[28px]"
                             onClick={() => window.dispatchEvent(new CustomEvent('toggle-display', { detail: { message: 'header-account' } }))}>
                            <i className={`${section.account_icon || 'icon-user-circle'} mobile_menu-icon`}></i>
                        </div>
                    )
                )}

            {/* Account - Not Logged In without Popup */}
            {!section.use_account_popup &&
                <>
                    {(isMobile || !section.show_text_no_icons_account)
                        ? <a href="/account" className="account-icon leading-3 antialiased [&>svg]:h-[28px]">
                            <i className={`${section.account_icon || 'icon-user-circle'} mobile_menu-icon`}></i>
                          </a>
        
                        : <a href="/account" className={`${text_style} px-3 py-2.5`}>
                            {isCustomerLoggedIn ? 'My Account' : accountText}
                          </a>
                    }
                </>
            }

            {/* Cart Icon/Text */}
            <div
                className="flex items-center relative [&>svg]:h-[28px] cursor-pointer"
                onClick={toggleMinicart}
            >
                {section.show_text_no_icons_cart ? (
                    <span className={`${text_style}`}>{cartText}</span>
                ) : (
                    <i className={`${section.cart_icon || 'icon-shopping-cart-simple-thin'} mobile_menu-icon`}></i>
                )}
                
                {(cartItemCount) > 0 && (
                    <div className={`${section.cart_counter_font || 'b5'} font-semibold header-item-count`}>
                        <span>{cartItemCount}</span>
                    </div>
                )}
            </div>
        </div>
    );
};
